import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTutorial } from "@/hooks/useTutorial";
import { ExternalLink, Check, FileText } from "lucide-react";

interface WordAddinModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WordAddinModal({ isOpen, onClose }: WordAddinModalProps) {
  const { completeStep } = useTutorial();

  const handleComplete = async () => {
    await completeStep("word_addin");
    onClose();
  };

  const handleOpenStore = () => {
    window.open(
      "https://marketplace.microsoft.com/es-es/product/saas/wa200008068?tab=overview",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-6 w-6 text-primary" />
            Instalá DoctIA en Word
          </DialogTitle>
          <DialogDescription>
            Buscá jurisprudencia sin salir de tu documento
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Screenshot placeholder - using a styled div since we don't have the actual image */}
          <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30">
            <img 
              src="/images/help/doctia-word-addin.jpg" 
              alt="DoctIA Add-in en Microsoft Word" 
              className="w-full h-auto"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/80 to-transparent p-4">
              <p className="text-sm text-foreground font-medium">
                El Add-in aparece en la barra lateral de Word
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
              <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">Búsqueda integrada</p>
                <p className="text-sm text-muted-foreground">
                  Buscá fallos y doctrinas directamente desde Word
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
              <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">Inserción con un clic</p>
                <p className="text-sm text-muted-foreground">
                  Insertá citas formateadas en tu documento automáticamente
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
              <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">Misma cuenta</p>
                <p className="text-sm text-muted-foreground">
                  Usá tu cuenta de DoctIA existente
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button 
              onClick={handleOpenStore}
              className="flex-1 gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Ir a Microsoft Store
            </Button>
            <Button 
              variant="outline"
              onClick={handleComplete}
              className="flex-1"
            >
              Lo veré después
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
